// "Did that cast cost a slot?" A player who casts through Beyond20 (or just says it) may never tick the slot on
// their sheet. Owner, 2026-10-08: players sometimes fudge their slots, so the DM wants to SEE it. This never
// writes anything by itself: it notices a levelled cast with no slot spent, and offers the DM a one-click fix.
import { parseCastTitle } from "./effects";
import { slotView } from "./spellSlots";
import { spellInfo } from "./spellRules";
import { normName as norm } from "./text";
import type { Character } from "./types";

export interface Cast { character: Character; spell: string; level: number }

const levelOfInfo = (name: string): number => {
  const m = String(spellInfo(name)?.level ?? "").match(/\d+/);
  return m ? Number(m[0]) : 0;
};

/** A levelled spell cast by a party member, read from a card title; null for a cantrip, a non-spell, or a stranger. */
export function castFromCard(title: string, roster: Character[]): Cast | null {
  const parsed = parseCastTitle(title);
  if (!parsed) return null;
  const who = norm(parsed.caster);
  const character = roster.find((c) => norm(c.name) === who) || roster.find((c) => norm(c.name).split(" ")[0] === who.split(" ")[0] && who.length > 0);
  if (!character || !character.id.startsWith("ddb-")) return null;
  const known = (character.spellbook || []).find((s) => norm(s.name) === norm(parsed.spell));
  const level = known ? Number(known.level) || 0 : levelOfInfo(parsed.spell);
  // "Longsword (Edam)" is an attack card that parses the same way: only a spell the sheet or the table knows counts.
  if (!known && !spellInfo(parsed.spell)) return null;
  return level >= 1 ? { character, spell: parsed.spell, level } : null;
}

export const totalUsed = (c: Pick<Character, "spellSlotsUsed">) => (c.spellSlotsUsed || []).reduce((n, s) => n + (Number(s.used) || 0), 0);

/** After the wait: true when NO slot has been spent since the cast was seen. */
export const slotMissing = (usedAtCast: number, now: Pick<Character, "spellSlotsUsed">) => totalUsed(now) <= usedAtCast;

/** The cheapest slot that could have paid for it: the spell's own level, else the next level up with one free. */
export function slotToSpend(c: Character, spellLevel: number): number | null {
  const v = slotView(c);
  if (!v.known) return null;
  return v.rows.filter((r) => r.level >= spellLevel && r.used < r.max).sort((a, b) => a.level - b.level)[0]?.level ?? null;
}
